import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import AnnouncementBanner from '../../components/ui/AnnouncementBanner';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { paidAuditService } from '../../services/paidAuditService';
import { invoiceGenerator } from '../../utils/invoiceGenerator';

/* ─── Status badge config ───────────────────────────────────────────────── */
const STATUS_CONFIG = {
  captured:   { color: 'bg-success/10 text-success',     label: 'Paid',       icon: 'CheckCircle2' },
  authorized: { color: 'bg-primary/10 text-primary',     label: 'Authorized', icon: 'Clock'        },
  pending:    { color: 'bg-warning/10 text-warning',     label: 'Pending',    icon: 'Clock'        },
  failed:     { color: 'bg-error/10 text-error',         label: 'Failed',     icon: 'XCircle'      },
  refunded:   { color: 'bg-muted text-muted-foreground', label: 'Refunded',   icon: 'RotateCcw'    },
};

const ProfileScreen = () => {
  const { user, userProfile, currentRole, getClientForRole } = useAuth();

  const [isEditing,  setIsEditing]  = useState(false);
  const [isSaving,   setIsSaving]   = useState(false);
  const [message,    setMessage]    = useState({ type: '', text: '' });

  const [paymentHistory,    setPaymentHistory]    = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);

  const [formData, setFormData] = useState({
    fullName:    userProfile?.full_name     || '',
    email:       user?.email               || '',
    companyName: userProfile?.company_name  || '',
    jobTitle:    userProfile?.job_title     || '',
    mobile:      userProfile?.mobile        || '',
  });

  const [notifications, setNotifications] = useState({
    auditCompletion:   true,
    systemUpdates:     true,
    promotionalEmails: false,
  });

  /* ── Sync formData when userProfile loads ── */
  useEffect(() => {
    if (userProfile) {
      setFormData({
        fullName:    userProfile.full_name    || '',
        email:       user?.email             || '',
        companyName: userProfile.company_name || '',
        jobTitle:    userProfile.job_title    || '',
        mobile:      userProfile.mobile       || '',
      });
    }
  }, [userProfile, user]);

  /* ── Fetch payment history ── */
  const fetchPaymentHistory = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoadingPayments(true);
      const payments = await paidAuditService?.getUserPaymentOrders(user.id);
      setPaymentHistory(payments || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
    } finally {
      setIsLoadingPayments(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchPaymentHistory(); }, [fetchPaymentHistory]);

  /* ── Derived stats ── */
  const capturedPayments   = paymentHistory.filter(p => p?.payment_status === 'captured');
  const totalPaid          = capturedPayments.length;
  const memberSince        = new Date(userProfile?.created_at || Date.now())
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const totalPresentations = paymentHistory.length > 0 ? paymentHistory.length : '—';

  const accountStats = [
    { label: 'Total Audits', value: isLoadingPayments ? '…' : totalPresentations, icon: 'FileText'   },
    { label: 'Paid Audits',  value: isLoadingPayments ? '…' : totalPaid,          icon: 'TrendingUp' },
    { label: 'Member Since', value: memberSince,                                   icon: 'Calendar'   },
  ];

  /* ── Helpers ── */
  const handleDownloadInvoice = (payment) => {
    invoiceGenerator?.downloadInvoicePDF(payment, userProfile);
  };

  const getPaymentStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${cfg.color} flex items-center gap-1.5 w-fit`}>
        <Icon name={cfg.icon} size={12} />
        {cfg.label}
      </span>
    );
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'INR' ? '₹' : '$';
    return `${symbol}${(amount / 100).toFixed(2)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNotificationChange = (name) => {
    setNotifications(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const client = getClientForRole(currentRole);
      const { error } = await client
        ?.from('user_profiles')
        ?.update({
          full_name:    formData.fullName,
          company_name: formData.companyName,
          job_title:    formData.jobTitle,
          mobile:       formData.mobile || null,
        })
        ?.eq('id', user?.id);
      if (error) throw error;
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setIsEditing(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error?.message || 'Failed to update profile' });
    } finally {
      setIsSaving(false);
    }
  };


  /* ── Toggle switch ── */
  const Toggle = ({ on, onChange }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-primary' : 'bg-muted'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );

  return (
    <>
      <Helmet>
        <title>Profile - DecX AI</title>
        <meta name="description" content="Manage your myDeckAudit profile and account settings" />
      </Helmet>

      {/* Fixed header */}
      <RoleBasedHeader />

      {/* Announcement banner — normal flow, no gap */}
      <AnnouncementBanner />

      <main className="pb-8 min-h-screen bg-background">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 max-w-5xl">

          {/* Page header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Profile Settings</h1>
            <p className="text-muted-foreground">Manage your personal account information and preferences</p>
          </div>

          {/* Status message */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
              message.type === 'success'
                ? 'bg-success/10 border border-success/20'
                : 'bg-error/10 border border-error/20'
            }`}>
              <Icon
                name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${message.type === 'success' ? 'text-success' : 'text-error'}`}
              />
              <p className={`text-sm ${message.type === 'success' ? 'text-success' : 'text-error'}`}>
                {message.text}
              </p>
            </div>
          )}

          <div className="space-y-6">

            {/* ── Account Statistics ── */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="BarChart3" size={20} />
                Account Statistics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {accountStats.map((stat, i) => (
                  <div key={i} className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name={stat.icon} size={20} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                        <p className="text-sm text-muted-foreground">{stat.label}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Payment History ── */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="CreditCard" size={20} />
                Payment History
              </h2>

              {isLoadingPayments ? (
                <div className="flex items-center justify-center py-8 gap-2">
                  <Icon name="Loader2" className="w-6 h-6 animate-spin text-primary" />
                  <span className="text-muted-foreground">Loading payment history...</span>
                </div>
              ) : paymentHistory.length > 0 ? (
                <div className="space-y-3">
                  {paymentHistory.map(payment => (
                    <div key={payment?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">

                        {/* Left: title + meta */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Icon name="FileText" size={16} className="text-primary flex-shrink-0" />
                            <span className="font-medium text-foreground">Paid Deck Audit</span>
                            {getPaymentStatusBadge(payment?.payment_status)}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Icon name="Calendar" size={14} />
                              <span>
                                {new Date(payment?.paid_at || payment?.created_at)
                                  ?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                              <Icon name="Hash" size={14} className="flex-shrink-0" />
                              <span className="text-xs truncate">
                                {payment?.razorpay_payment_id || payment?.razorpay_order_id?.slice(0, 20)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right: amount + download */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-xl font-bold text-foreground">
                              {formatCurrency(payment?.amount, payment?.currency)}
                            </p>
                            <p className="text-xs text-muted-foreground">{payment?.currency}</p>
                          </div>
                          {payment?.payment_status === 'captured' && (
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="Download"
                              onClick={() => handleDownloadInvoice(payment)}
                            >
                              Invoice
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Linked submission */}
                      {payment?.deck_submissions && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Icon name="Link" size={12} />
                            Linked to:{' '}
                            <span className="font-medium text-foreground">
                              {payment.deck_submissions.title ||
                                `Submission ${payment.deck_submissions.id?.slice(0, 8)}`}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="CreditCard" size={48} className="mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No payment history yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Your paid audit purchases will appear here</p>
                </div>
              )}
            </div>

            {/* ── Personal Information ── */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Icon name="User" size={20} />
                  Personal Information
                </h2>
                {!isEditing && (
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} iconName="Edit">
                    Edit
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                <Input
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter your full name"
                />
                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  disabled
                  description="Email cannot be changed"
                />
                <Input
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter your company name"
                />
                <Input
                  label="Job Title"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter your job title"
                />
                <Input
                  label="Mobile Number"
                  name="mobile"
                  type="tel"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  disabled={!isEditing}
                  placeholder="Enter your mobile number"
                />
              </div>

              {isEditing && (
                <div className="flex gap-3 mt-6">
                  <Button variant="default" onClick={handleSaveProfile} loading={isSaving} iconName="Save">
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditing(false);
                      setFormData({
                        fullName:    userProfile?.full_name    || '',
                        email:       user?.email              || '',
                        companyName: userProfile?.company_name || '',
                        jobTitle:    userProfile?.job_title    || '',
                        mobile:      userProfile?.mobile       || '',
                      });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>

            {/* ── Notification Preferences ── */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
                <Icon name="Bell" size={20} />
                Notification Preferences
              </h2>

              <div className="space-y-4">
                {[
                  { key: 'auditCompletion',   title: 'Audit Completion',   desc: 'Receive notifications when your audit is complete' },
                  { key: 'systemUpdates',     title: 'System Updates',     desc: 'Get notified about system updates and new features' },
                  { key: 'promotionalEmails', title: 'Promotional Emails', desc: 'Receive promotional offers and marketing communications' },
                ].map(({ key, title, desc }, i, arr) => (
                  <div
                    key={key}
                    className={`flex items-center justify-between py-3 ${i < arr.length - 1 ? 'border-b border-border' : ''}`}
                  >
                    <div className="pr-4">
                      <p className="font-medium text-foreground">{title}</p>
                      <p className="text-sm text-muted-foreground">{desc}</p>
                    </div>
                    <Toggle on={notifications[key]} onChange={() => handleNotificationChange(key)} />
                  </div>
                ))}
              </div>
            </div>

            {/* ── Account Security ── */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
                <Icon name="Shield" size={20} />
                Account Security
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                  <Icon name="CheckCircle" className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Account Status: Active</p>
                    <p className="text-sm text-muted-foreground">Your account is secure and verified</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                  <Icon name="Mail" className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Email Verified</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>
    </>
  );
};

export default ProfileScreen;